import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import RichText from "@/lib/richText";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CornerDownRight, Trash2, ThumbsUp } from "lucide-react";
import FlagButton from "@/components/FlagButton";
import UserLink from "@/components/UserLink";
import TitleTag from "@/components/TitleTag";
import TierPortal from "@/components/TierPortal";
import PostMedia from "@/components/PostMedia";
import MiniComposer from "@/components/MiniComposer";
import AuthorThankTip from "@/components/AuthorThankTip";

export const REACTION_EMOJIS = ["👍", "❤️", "🔥", "😂", "😢", "😮", "💯"];

export function timeAgo(iso) {
    try {
        const d = new Date(iso);
        const s = Math.floor((Date.now() - d.getTime()) / 1000);
        if (s < 60) return "just now";
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
}

export function AuthorAvatar({ photo, name, count = 0, size = 18 }) {
    return (
        <TierPortal count={count} size={size} ringW={2} gap={1} testid="board-author-avatar">
            {photo ? (
                <img src={photo} alt="" className="h-full w-full object-cover" />
            ) : (
                <span className="h-full w-full flex items-center justify-center bg-[#1a1a1a] text-[9px] text-[#ff4500]">{(name || "?")[0].toUpperCase()}</span>
            )}
        </TierPortal>
    );
}

export function ReactionBar({ post, accent }) {
    const { user } = useAuth();
    const [state, setState] = useState(post.reactions || { counts: {}, total: 0, mine: null });
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const pressTimer = useRef(null);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const total = state.total || Object.values(state.counts || {}).reduce((a, b) => a + b, 0);

    const react = async (emoji) => {
        if (!user) return toast.error("Log in to react");
        try {
            if (state.mine === emoji) {
                const { data } = await api.delete(`/boards/posts/${post.id}/reactions`);
                setState(data);
            } else {
                const { data } = await api.post(`/boards/posts/${post.id}/reactions`, { emoji });
                setState(data);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not react");
        }
        setOpen(false);
    };

    const startPress = () => { pressTimer.current = setTimeout(() => setOpen(true), 400); };
    const endPress = () => { if (pressTimer.current) clearTimeout(pressTimer.current); };

    return (
        <div className="relative inline-flex group" ref={ref} onMouseLeave={() => setOpen(false)} data-testid={`board-reactions-${post.id}`}>
            <button
                type="button"
                data-testid={`reaction-trigger-${post.id}`}
                onClick={() => setOpen((o) => !o)}
                onTouchStart={startPress}
                onTouchEnd={endPress}
                title="React (hover for more)"
                className="group inline-flex items-center gap-1.5 text-[13px] transition-colors"
                style={state.mine ? { color: accent } : undefined}
            >
                <span className="flex h-8 w-8 items-center justify-center rounded-full transition-colors group-hover:bg-white/5">
                    {state.mine ? <span className="text-[18px] leading-none">{state.mine}</span> : <ThumbsUp className="h-[18px] w-[18px]" />}
                </span>
                {total > 0 && <span>{total}</span>}
            </button>
            <div className={`absolute left-0 bottom-full pb-2 z-40 ${open ? "block" : "hidden group-hover:block"}`} data-testid={`reaction-palette-${post.id}`}>
                <div className="flex items-center gap-1 rounded-full border border-[#2a2a2a] bg-[#0f0f0f] px-2 py-1.5 shadow-2xl">
                    {REACTION_EMOJIS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            data-testid={`reaction-${emoji}-${post.id}`}
                            onClick={() => react(emoji)}
                            className={`text-xl leading-none transition-transform hover:scale-125 ${state.mine === emoji ? "scale-110" : ""}`}
                            title={emoji}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function buildReplyTree(replies) {
    const byId = {};
    const roots = [];
    replies.forEach((r) => { byId[r.id] = { reply: r, children: [] }; });
    replies.forEach((r) => {
        const pid = r.parent_reply_id;
        if (pid && byId[pid]) byId[pid].children.push(byId[r.id]);
        else roots.push(byId[r.id]);
    });
    return roots;
}

function ReplyRow({ node, depth, accent, canModerate, isExpanded, onToggle, isReplying, onToggleReply, onDeleteReply, onSubmitReply }) {
    const { user } = useAuth();
    const r = node.reply;
    const kids = node.children || [];
    const canDel = user && (user.id === r.author_id || canModerate);

    const submitChild = async (payload) => {
        await onSubmitReply({ ...payload, parent_reply_id: r.id });
        onToggleReply(null);
    };

    return (
        <div style={{ marginLeft: depth * 20 }} className={depth > 0 ? "pl-3 border-l border-[#2a2a2a]" : ""} data-testid={`board-reply-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center gap-1.5">
                        <UserLink username={r.author_username}><AuthorAvatar photo={r.author_photo} name={r.author_username} count={r.author_thanks_count || 0} size={16} /></UserLink>
                        <UserLink username={r.author_username} prefix={false} />
                        <TitleTag title={r.author_title} isPro={r.author_is_pro} />
                        {r.author_id && <AuthorThankTip userId={r.author_id} username={r.author_username} tipLinks={r.author_tip_links} thanksCount={r.author_thanks_count || 0} contextType="comment" contextId={r.id} />}
                        <span className="ml-auto whitespace-nowrap">{timeAgo(r.created_at)}</span>
                    </div>
                    {r.body && <div className="text-sm text-[#cfcfcf] whitespace-pre-line break-words"><RichText text={r.body} /></div>}
                    <PostMedia images={r.images} gif={r.gif} videos={r.videos} />
                    <div className="mt-1.5 flex items-center gap-4">
                        <ReactionBar post={{ id: r.id, reactions: r.reactions }} accent={accent} />
                        {user && (
                            <button type="button" data-testid={`board-reply-respond-${r.id}`} onClick={() => onToggleReply(isReplying ? null : r.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] hover:opacity-80" style={{ color: accent }}>
                                <CornerDownRight className="h-3 w-3" /> Reply
                            </button>
                        )}
                        {kids.length > 0 && (
                            <button type="button" data-testid={`board-reply-children-toggle-${r.id}`} onClick={() => onToggle(r.id)} className="text-[11px] uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-white">
                                {isExpanded ? "Hide" : "Show"} {kids.length} {kids.length === 1 ? "reply" : "replies"}
                            </button>
                        )}
                    </div>
                    {isReplying && user && (
                        <div className="mt-1">
                            <MiniComposer onSubmit={submitChild} placeholder={`Reply to @${r.author_username}…`} testidPrefix={`board-reply-composer-${r.id}`} accent={accent} autoFocus />
                        </div>
                    )}
                </div>
                {canDel ? (
                    <button type="button" data-testid={`board-reply-delete-${r.id}`} onClick={() => onDeleteReply(r.id)} className="text-[#a0a0a0] hover:text-red-500 shrink-0" aria-label="Delete reply">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                ) : (
                    <FlagButton targetType="reply" targetId={r.id} label="" testid={`board-reply-flag-${r.id}`} />
                )}
            </div>
        </div>
    );
}

// Flatten the reply tree into an ordered list with depth so we can render it without a
// self-referential JSX component (which the visual-edit babel plugin cannot traverse).
function flattenReplies(nodes, expanded, depth, out) {
    for (const n of nodes) {
        out.push({ node: n, depth });
        if (n.children.length && expanded.has(n.reply.id)) flattenReplies(n.children, expanded, depth + 1, out);
    }
}

export function RepliesPanel({ replies, accent, canModerate, onDeleteReply, onSubmitReply, testid }) {
    const [expanded, setExpanded] = useState(() => new Set());
    const [replyingTo, setReplyingTo] = useState(null);

    const toggle = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const submitAndExpand = async (payload) => {
        await onSubmitReply(payload);
        if (payload.parent_reply_id) setExpanded((prev) => new Set(prev).add(payload.parent_reply_id));
    };

    const flat = [];
    flattenReplies(buildReplyTree(replies || []), expanded, 0, flat);

    return (
        <div className="space-y-2" data-testid={testid}>
            {flat.map(({ node, depth }) => (
                <ReplyRow
                    key={node.reply.id}
                    node={node}
                    depth={depth}
                    accent={accent}
                    canModerate={canModerate}
                    isExpanded={expanded.has(node.reply.id)}
                    onToggle={toggle}
                    isReplying={replyingTo === node.reply.id}
                    onToggleReply={setReplyingTo}
                    onDeleteReply={onDeleteReply}
                    onSubmitReply={submitAndExpand}
                />
            ))}
        </div>
    );
}
